<script>
	import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
	import mapbox from "mapbox-gl";
	import { onDestroy,onMount } from "svelte";
	import { get } from "svelte/store";
	import { MAPBOX_TOKEN } from "../../config/mapbox";
	import AlertsStore from "../Alerts/Store";
	import AppStore from "../AppStore";
	import AuthStore from "../Auth/Store";
	import IdentityHelper from "../Branding/IdentityHelper";
	import GeoHelper from "../Helpers/GeoHelper";
	import InputHelper from "../Helpers/InputHelper";
	import roundFloat from "../Helpers/Misc/roundFloat";
	import LoadingStore from "../LoadingStore";
	import VesselProperty from "../Models/Enums/VesselProperty";
	import RoutePlottingStore from "../UI/MeasureTool/RoutePlottingStore";
	import PortsStore from "../UI/Ports/Store";
	import VesselHistoryStore from "../UI/VesselHistory/Store";
	import VesselsNearbyStore from "../UI/VesselsNearby/Store";
	import CustomMapDatasets from "./CustomMapDatasets";
	import MapControlType from "./Enums/MapControlType";
	import MapLayer from "./Enums/MapLayer";
	import VisibilityState from "./Enums/VisibilityState";
	import ExtendedMapControlsMunge from "./ExtendedMapControlsMunge.svelte";
	import "./Map.scss";
	import MapLayerManager from "./MapLayerManager";
	import MapVesselSelectionCorners from "./MapVesselSelectionCorners.svelte";
	import Popup from "./Popup/Popup.svelte";
	import MapStore from "./Store";
			
	let container;
	let map;

	let containerWidth = window.innerWidth + "px";
	let containerHeight = window.innerHeight + "px";

	const mapStyles = {
		blue: "mapbox://styles/geollectryan/ckb2fkokp151s1ipscyj41vra",
		grey: "mapbox://styles/geollectryan/ckell3jgs1ff419pkqxxqglai"
	};

	function allGeocoder(query) {
		// Hook into Searching LocalPorts
		return PortsStore.helpers.geocoderSearch(query);

		// TODO: It would be really awesome if we could return a promise here for async results.
		//		 However... mapbox doesn't let us do this. Consider forking it or building our own search box.
		// return new Promise((resolve, reject) => {
		// 	setTimeout(resolve([AllSearchableData[0], AllSearchableData[1]]), 1000);
		// });
	}

	onDestroy(()=>{
		AlertsStore.actions.removeAllVessels(); // remove vessels before removing map.
		MapStore.meta.initialised.set(false);
		MapStore.mapbox.instance.set(null);
		CustomMapDatasets.destroy();
	});

	let lng = MapStore.homeLocation.lng;
	let lat = MapStore.homeLocation.lat;
	let zoom = MapStore.homeLocation.zoom;

	onMount(() => {

		$lng = get(AuthStore.session.company).mapHomeLng;
		$lat = get(AuthStore.session.company).mapHomeLat;
		$zoom = get(AuthStore.session.company).mapHomeZoom;
		let isGeollectGrey = IdentityHelper.current().name === "Geollect";

		map = new mapbox.Map({
			container,
			accessToken: MAPBOX_TOKEN,
			style: (isGeollectGrey)?mapStyles.grey:mapStyles.blue,
			center: [$lng, $lat],
			zoom: $zoom //,
			// maxBounds:
		});
		map.setMaxPitch(0);

		MapStore.mapbox.instance.set(map);

		const geocoder = new MapboxGeocoder({
			accessToken: MAPBOX_TOKEN,
			marker: {
				color: "orange"
			},
			localGeocoder: allGeocoder,
			limit: 12,
			mapboxgl: mapbox
		});
		map.addControl(geocoder);
		MapStore.mapbox.instanceGeocoder.set(geocoder);
		map.addControl(new mapbox.NavigationControl());

		map.on("load", async () => {
			map.resize();
			map.getCanvas().style.cursor = MapStore.helpers.getDefaultCursor();
			map.getCanvas().style.outline = "none";

			const canvasContainer = map.getCanvasContainer();
			canvasContainer.style.outline = "none";


			let mouseDown = (event) => {
				if (event.defaultPrevented) { return; }

				const control = get(MapStore.control);
				if (control === MapControlType.PAN) {
					// ... no change in behaviour.
				}
				else if (control === MapControlType.BOX_SELECT) {
					MapStore.controlData.boxSelect.mouseDown(map, event);
				}
				else if (control === MapControlType.BOX_PICKER) {
					MapStore.controlData.boxPicker.obj.mouseDown(map, event);
				}
			};
			map.on("mousedown", mouseDown);
			map.on("touchstart", mouseDown);

			let mouseUp = (event) => {
				const control = get(MapStore.control);
				if (control === MapControlType.PAN) {
					// ... no change in behaviour.
				}
				else if (control === MapControlType.LNGLAT_PICKER) {
					if (get(MapStore.controlData.lngLatPickerAuto)) {
						MapStore.controlData.lngLatPicker.set({
							lng: roundFloat(event.lngLat.lng, 4),
							lat: roundFloat(event.lngLat.lat, 4)
						});
					}
					// .....
					let lngLatFormat = get(AuthStore.session.user).lngLatFormat;
					let lngLatStr = GeoHelper.lngLatCoordinatesToString([event.lngLat.lng, event.lngLat.lat], lngLatFormat, 4);
					InputHelper.copyText(lngLatStr);
					MapStore.control.set(MapControlType.PAN);
					AppStore.snackbar.start("Lnglat copied to clipboard!", "success");
				}
				else if (control === MapControlType.BOX_SELECT) {
					MapStore.controlData.boxSelect.mouseUp(map, event);
				}
				else if (control === MapControlType.BOX_PICKER) {
					MapStore.controlData.boxPicker.obj.mouseUp(map, event);
				}
				else if (control == MapControlType.ADD_POLYLINE ||
						 control == MapControlType.ADD_POLYGON) {
					event.preventDefault();
					let currentPoints = get(MapStore.controlData.draw.points);
					currentPoints.push(event.lngLat);
					MapStore.controlData.draw.points.set(currentPoints);
				}
				else if (control == MapControlType.ADD_MARKER) {
					let currentMarkers = get(MapStore.controlData.draw.markers);
					currentMarkers.push({
						...event.lngLat,
						properties: {}
					});
					MapStore.controlData.draw.markers.set(currentMarkers);
				}
			};

			map.on("mouseup", mouseUp);
			map.on("touchend", mouseUp);

			let mouseMove = (event) => {
				let lat = Math.round(event.lngLat.lat * 1000) / 1000;
				let lng = Math.round(event.lngLat.lng * 1000) / 1000;
				MapStore.actions.setCursorLngLat(lng, lat);

				const control = get(MapStore.control);

				if (control === MapControlType.BOX_SELECT) {
					MapStore.controlData.boxSelect.mouseMove(map, event);
				}
				else if (control === MapControlType.BOX_PICKER) {
					MapStore.controlData.boxPicker.obj.mouseMove(map, event);
				}
			};
			map.on("mousemove", mouseMove);
			map.on("touchmove", mouseMove);

			// Move mapbox's overlay elements
			document.getElementById("map-overlay").append(document.querySelector(".mapboxgl-control-container"));

			MapLayerManager.init(map)
				.then(() => {

					MapStore.meta.initialised.set(true);

					const buildingsLayer = map.getLayer("building");
					if (buildingsLayer) {
						map.setLayerZoomRange("building", 12, 22);
						map.setPaintProperty("building", "fill-color", "#cccccc");
						map.setPaintProperty("building", "fill-opacity", [
							"interpolate", ["linear"],
							["zoom"],
							12, 1.0,
							18, 0.0
						]);

						map.setLayerZoomRange("building-outline", 12, 22);
						map.setPaintProperty("building-outline", "line-color", "#aaaaaa");
						map.setPaintProperty("building-outline", "line-opacity", [
							"interpolate", ["linear"],
							["zoom"],
							12, 0.5,
							18, 1.0
						]);
						map.setPaintProperty("building-outline", "line-width", [
							"interpolate", ["linear"],
							["zoom"],
							12, 0.5,
							20, 2.0
						]);
					}

					MapStore.controlData.boxSelect.end.subscribe((end)=>{
						const bbox = MapStore.controlData.boxSelect.getBBoxPixels();

						const groups = [
							AlertsStore.vesselGroup,
							VesselHistoryStore.vesselGroup,
							VesselsNearbyStore.results.vesselGroup
						];

						const layers = [
								MapLayer.VESSELS_LIVE,
								MapLayer.VESSELS_NEARBY,
								MapLayer.VESSEL_HISTORY_SNAPSHOT
							].filter((item) => {
								return map.getLayer(item) && map.getLayoutProperty(item, "visibility") === VisibilityState.VISIBLE;
							});

						let features = map.queryRenderedFeatures(bbox, {
							layers: layers
						});

						let identifiers = {
							[VesselProperty.IMO]: [],
							[VesselProperty.MMSI]: [],
							[VesselProperty.SPIRE_ID]: []
						}
						for(let i = 0; i < features.length; i++) {
							if (typeof features[i].properties[VesselProperty.IMO] !== "undefined") identifiers[VesselProperty.IMO].push(features[i].properties[VesselProperty.IMO]);
							if (typeof features[i].properties[VesselProperty.MMSI] !== "undefined") identifiers[VesselProperty.MMSI].push(features[i].properties[VesselProperty.MMSI]);
							if (typeof features[i].properties[VesselProperty.SPIRE_ID] !== "undefined") identifiers[VesselProperty.SPIRE_ID].push(features[i].properties[VesselProperty.SPIRE_ID]);
						}
						for(let i = 0; i < groups.length; i++) {
							if (!groups[i]) {
								continue;
							}
							if (!InputHelper.isCtrlKeyDown()) {
								groups[i].deselectAll();
							}
							const idTypeProperty = groups[i].uniqidType;
							const vessels = groups[i].filter((vessel)=>{
								return identifiers[idTypeProperty].indexOf(vessel[idTypeProperty]) >= 0;
							});
							for (let j = 0; j < vessels.length; j++) {
								vessels[j].selected = true;
							}
							groups[i].update();
						}


						// TODO: this should work on History/Nearby layers too.
						// AlertsStore.actions.setVesselsSelectedByIMOs(imos, true, !get(CtrlKeyDown));
					});
				})
		});

		map.resize();

		LoadingStore.add("map_rois", MapStore.rois.loadState);
		return () => {
			LoadingStore.remove("map_rois");
			map.remove();
		};
	});

	let appTab = AppStore.menu.tab;
	let routePlotVisible = RoutePlottingStore.meta.visible;
	const sanctionsVisible = MapStore.sanctionsVisible;
	const timezonesVisible = MapStore.timezoneVisible;
	let view = MapStore.view;
	$: {
		$appTab;
		$view;
		$routePlotVisible;
		$sanctionsVisible;
		$timezonesVisible;
		MapLayerManager.updateVisibleLayers();
	}



</script>


<div bind:this={container} id="map">
	{#if map}
		<slot />
		<MapVesselSelectionCorners/>
		<Popup />
		<ExtendedMapControlsMunge />
	{/if}
</div>
